<template>
  <div class="nav container container--fluid"
       :class="{ 'nav--sticky': sticky }"
  >
    <div class="nav__main">
      <router-link :to="logoLink"
                   class="nav__logo"
                   data-test-id="navbar-logo"
      >
        <img v-if="showLogo"
             alt="Bistroo"
             src="/assets/img/brand/bistroo-logo.svg"
             width="126"
             data-not-lazy
        >
      </router-link>
      <div class="nav__menu">
        <ul class="nav__menu-items">
          <li class="nav__menu-item"
              data-test-id="user-account-button-mobile"
          >
            <ClientOnly>
              <!-- Mobile user button -->
              <UserAccountButtonContainer inline/>
            </ClientOnly>
          </li>

          <!-- Mobile checkout button -->
          <li v-if="!route.name || !route.name.includes('checkout')"
              v-taptic="'selection'"
              class="nav__menu-item"
              data-test-id="checkout-button-mobile"
              @click="router.push(localizedRoute({ name: 'checkout' }))"
          >
            <Icon name="cart"/>
          </li>
          <li v-show="showFiltersButton"
              v-taptic="'selection'"
              class="nav__menu-item"
              @click="closeFiltersTooltip"
          >
            <Icon class="nav__filters" name="filter-active" v-if="hasActiveFilters"/>
            <Icon class="nav__filters" name="filter" v-if="!hasActiveFilters"/>
          </li>
          <li class="nav__menu-item" data-test-id="mobile-menu">
            <Tooltip>
              <template #default="{ active }">
                <Icon name="cross" v-if="active"/>
                <Icon name="menu" v-if="!active"/>
              </template>
              <template #content>
                <MenuTooltip/>
              </template>
            </Tooltip>
          </li>
        </ul>
      </div>
    </div>

    <!-- Toolbar when scrolling back up -->
    <div v-if="open"
         class="nav__options"
         data-test-id="navbar-mobile-options"
    >
      <div class="nav__divider">
        <Divider type="dashed"/>
      </div>
      <MobileOptions :has-active-filters="hasActiveFilters"
                     @clicked-distribution-type="toggleDistribution"
                     @clicked-filters="openFiltersTooltip">
        <template #address-value>
          {{ address }}
        </template>
        <template #dist-value>
          <template v-if="distribution === Distributions.Delivery">
            {{ t('terms.delivery') }}
          </template>
          <template v-else>
            {{ t('distribution-toggle.takeaway') }}
          </template>
        </template>
      </MobileOptions>
    </div>

    <!-- border bottom line -->
    <div class="nav__border-bottom"/>
  </div>
</template>

<script setup lang="ts">
import ClientOnly from '@/components/SSR/ClientOnly.vue'
import UserAccountButtonContainer from '@/components/Buttons/UserAccountButton/UserAccountButtonContainer.vue'
import MobileOptions from '@/components/Navigation/MobileOptions.vue'
import Divider from '@/components/partials/Divider.vue'
import Icon from '@/components/partials/Icon.vue'
import MenuTooltip from '@/components/Menu/MenuTooltip.vue'
import Tooltip from '@/components/partials/Tooltip.vue'
import { useResizeObserver } from '@/composables/resizeObserver'
import { useLocalizedRouter } from '@/composables/localizedRouter'
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue"
import Distributions from "@/config/Distributions"
import { useRoute, useRouter } from "vue-router"
import { useSessionStore } from "@/stores/useSessionStore"
import { useI18n } from "@/composables/I18n"
import { useMarketplaceStore } from "@/stores/ssr/useMarketplaceStore"
import { storeToRefs } from "pinia"

const sessionStore = useSessionStore()
const marketplaceStore = useMarketplaceStore()
const router = useRouter()
const route = useRoute()

const props = defineProps({
  borderBottom: { type: Boolean, default: false },
  logoLink: { type: Object, default: null },
})

const { val } = useResizeObserver()
const { localizedRoute } = useLocalizedRouter()

const scrollState = ref(0)
const isPastHeader = ref<boolean>(false)
const showFiltersButton = ref<boolean>(false)
const hasActiveFiltersOnReload = ref<boolean>(false)
const open = ref<boolean>(false)
const sticky = ref<boolean>(false)

const filtersTooltipOpen = ref<boolean>(false)
const hasActiveFilters = computed(() => marketplaceStore.filteredCategories.length > 0)
const { distribution } = storeToRefs(sessionStore)

const showLogo = computed(() => !__CAPACITOR__ ||
    route.name?.includes('slug') ||
    route.name?.includes('marketplace') ||
    route.name?.includes('customer') ||
    route.name?.includes('verified'))

const isOnAllowedPageForMobileOptions = computed(() => {
  const excludedPagePatterns = [ 'search' ]

  return [ 'marketplace', 'slug' ].some(
      (pattern) =>
          // check if the route name includes the allowedPattern
          route.name?.includes(pattern) &&
          !excludedPagePatterns.some((excluded) =>
              route.name?.includes(excluded),
          ),
  )
})

const { t } = useI18n()

const address = computed(() => {
  if (sessionStore.location === null) {
    return `${t('terms.loading')}...`
  }

  return sessionStore.location.street || sessionStore.location.city
})

watch(filtersTooltipOpen, () => {
  if (!route.name?.includes('marketplace-search') && open.value) {
    displayFiltersIconInMenu()
  }

  if (open) {
    open.value = false
    showFiltersButton.value = isPastHeader.value
  }
})

watch(() => route, () => {
  if (route.name?.includes('marketplace-search')) {
    displayFiltersIconInMenu()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', scrollDetect)
})

onMounted(() => {
  // only trigger on reload / refresh
  if (hasActiveFilters.value) {
    hasActiveFiltersOnReload.value = true
  }

  window.addEventListener('scroll', scrollDetect)
  window.addEventListener('resize', () => {
    open.value = false
  })

  if (route.name?.includes('search')) {
    open.value = false
    showFiltersButton.value = true
  }
})

function displayFiltersIconInMenu() {
  open.value = false
  showFiltersButton.value = true
}

function scrollDetect() {
  // if the tooltip is open, short out
  if (filtersTooltipOpen.value) {
    return
  }

  // on desktop devices, never show the navbar options
  // enable only on whitelisted pages
  if (val.value >= 992 || !isOnAllowedPageForMobileOptions.value) {
    open.value = false
    return
  }

  // Current scroll position
  const top = window.scrollY

  const rootFontSize = parseInt(
      window
          .getComputedStyle(document.body)
          .getPropertyValue('font-size')
          .slice(0, -2),
  )

  isPastHeader.value = top > rootFontSize * 3.5

  if (top <= rootFontSize * 3.5) {
    // up or above header
    showFiltersButton.value = false
    open.value = false
  } else if (top > scrollState.value) {
    // down
    open.value = false
    showFiltersButton.value = true
  } else {
    // up
    open.value = true
    showFiltersButton.value = false
  }

  // Set previous scroll position
  scrollState.value = top

  sticky.value = scrollState.value > 0
}

function toggleDistribution() {
  const newDistribution = distribution.value === Distributions.TakeAway
      ? Distributions.Delivery
      : Distributions.TakeAway

  sessionStore.setDistribution(newDistribution)
}

function openFiltersTooltip() {
  filtersTooltipOpen.value = true
}

function closeFiltersTooltip() {
  filtersTooltipOpen.value = false
}
</script>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

.nav {
  display: block;
  width:   100%;
  position: relative;
  padding: 0 1.5rem;

  @include lg-up {
    padding: 0;
    display: none;
  }

  &__main {
    width:   100%;
    display: flex;
    justify-content: space-between;
    padding: calc(1.125rem + env(safe-area-inset-top)) 0 1.125rem;

    @include lg-up {
      padding: .5rem 4rem;
    }
  }

  &__logo {
    @include lg-down {
      height: 2rem;

      img {
        height:    100%;
        width:     auto;
        user-select: none;
        transform: translateY(2%);
        cursor:    pointer;
      }
    }
  }

  &__menu {
    display: flex;
    align-content: center;

    @include lg-up {
      display: none;
    }
  }

  &__menu-items {
    display: flex;
    margin:  0 -0.75rem;
    list-style: none;
    padding: 0;
    cursor:  pointer;
  }

  &__menu-item {
    display: flex;
    justify-content: center;
    margin:  0 0.75rem;
  }

  &__filters {
    transform: scale(0.9);
  }

  &__options {
    padding-bottom: 1rem;

    @include lg-up {
      display: none;
    }
  }

  &__divider {
    margin-bottom: 1rem;
  }

  &__border-bottom {
    border-bottom: 1px solid rgba(36, 46, 74, 0.15);

    @include lg-up {
      display: none;
    }
  }

  &--sticky {
    @include shadow-md;
  }

  &:deep(.user-account-button span),
  &:deep(.tooltip),
  &:deep(.tooltip__element) {
    display: flex;
    justify-content: center;
  }
}
</style>
